import type { CSSProperties } from "react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Link } from "react-router";
import { ChevronDown, Pencil, Play, Square, Trash2, User, UserCheck, X } from "lucide-react";
import {
  APPOINTMENT_STATUSES,
  APPOINTMENT_STATUS_COLORS,
  APPOINTMENT_STATUS_LABELS,
  type AppointmentStatus,
} from "@bookdental/schemas";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@bookdental/ui";
import { cn } from "@/lib/cn";
import { usePodeMarcarChegada } from "@/lib/paciente-chegou";
import { STATUS_META } from "@/components/prosthesis/prosthesis-meta";
import type { OpenProsthesisOrderSummary } from "./prosthesis-order-summary";

/** Consulta nesses status não tem atendimento a começar nem a encerrar. */
const FORA_DE_ATENDIMENTO: AppointmentStatus[] = ["CANCELLED", "NO_SHOW"];

type Appointment = {
  id: string;
  patientId?: string | null;
  patientName: string;
  patientPhone?: string | null;
  dentistName?: string | null;
  sala?: { nome: string } | null;
  procedure: string;
  date: Date | string;
  startTime: string;
  endTime: string;
  status: AppointmentStatus;
  notes?: string | null;
  createdAt?: Date | string;
  openProsthesisOrders?: OpenProsthesisOrderSummary[];
};

type Props = {
  appointment: Appointment;
  /** True quando o compromisso é de OUTRA organização (visão "Todas"/"Organizações" da
   *  Agenda Pessoal) — esconde ações de edição/exclusão, mostra só "Ver ficha do paciente". */
  readOnly?: boolean;
  organizationName?: string | null;
  onEdit: () => void;
  onStatusChange: (newStatus: AppointmentStatus) => void;
  onDelete: () => void;
  onClose: () => void;
  /** Começa/encerra o atendimento — carimba a hora real, além de mudar o status. Ausente em
   *  consulta de outra organização. */
  onIniciarAtendimento?: () => void;
  onEncerrarAtendimento?: () => void;
  atendimentoPendente?: boolean;
};

export function AppointmentPopover({
  appointment,
  readOnly = false,
  organizationName,
  onEdit,
  onStatusChange,
  onDelete,
  onClose,
  onIniciarAtendimento,
  onEncerrarAtendimento,
  atendimentoPendente = false,
}: Props) {
  const chegada = usePodeMarcarChegada()(appointment.status, appointment.date);
  const dateObj = new Date(appointment.date);
  const phone = appointment.patientPhone?.replace(/\D/g, "");
  const whatsappUrl = phone
    ? `https://wa.me/${phone.startsWith("55") ? phone : `55${phone}`}`
    : null;
  const statusColor = APPOINTMENT_STATUS_COLORS[appointment.status];
  const formattedDate = format(dateObj, "EEEE, dd 'de' MMMM 'de' yyyy", { locale: ptBR });
  const capDate = formattedDate.charAt(0).toUpperCase() + formattedDate.slice(1);

  return (
    <div className="w-[320px]">
      <div className="relative">
        <div className="space-y-2">
          {/* Status: dropdown editável, ou badge estático em compromisso de outra organização */}
          <div className="flex justify-center">
            {readOnly ? (
              <span
                className="inline-flex h-6 items-center gap-1 rounded-full px-2 text-[10px] font-bold uppercase tracking-wide"
                style={{
                  backgroundColor: `${statusColor}1f`,
                  color: statusColor,
                  boxShadow: `inset 0 0 0 1.5px ${statusColor}`,
                }}
              >
                <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: statusColor }} />
                {APPOINTMENT_STATUS_LABELS[appointment.status]}
              </span>
            ) : (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    className="inline-flex h-6 items-center gap-1 rounded-full px-2 text-[10px] font-bold uppercase tracking-wide shadow-sm transition-all hover:shadow-md"
                    style={{
                      backgroundColor: `${statusColor}1f`,
                      color: statusColor,
                      boxShadow: `inset 0 0 0 1.5px ${statusColor}`,
                    }}
                  >
                    <span
                      className="h-1.5 w-1.5 rounded-full"
                      style={{ backgroundColor: statusColor }}
                    />
                    {APPOINTMENT_STATUS_LABELS[appointment.status]}
                    <ChevronDown className="h-3 w-3 opacity-70" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="center"
                  sideOffset={6}
                  className="z-[60] min-w-[220px] border-zinc-200 bg-white p-1 shadow-xl dark:border-border dark:bg-popover"
                >
                  {APPOINTMENT_STATUSES.map((s) => (
                    <StatusItem
                      key={s}
                      status={s}
                      current={appointment.status}
                      onSelect={onStatusChange}
                    />
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>

          {/* Paciente sem cadastro (encaixe) não tem ficha pra abrir — aí fica só o texto. */}
          {appointment.patientId ? (
            <Link
              to={`/pacientes/${appointment.patientId}`}
              className="block text-[15px] font-bold leading-tight text-brand-700 underline-offset-2 hover:underline dark:text-primary"
            >
              {appointment.patientName}
            </Link>
          ) : (
            <p className="text-[15px] font-bold leading-tight text-zinc-900 dark:text-foreground">
              {appointment.patientName}
            </p>
          )}
        </div>
        <button
          type="button"
          onClick={onClose}
          className="absolute right-0 top-0 flex h-6 w-6 items-center justify-center rounded-md text-zinc-400 transition-colors hover:bg-zinc-100 hover:text-zinc-700 dark:hover:bg-muted dark:hover:text-foreground"
          aria-label="Fechar"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>

      <dl className={cn(GRADE, "mt-3 text-[12.5px]")}>
        <InfoLine label="Dia" value={<span className="font-bold text-zinc-900 dark:text-foreground">{capDate}</span>} />
        <InfoLine
          label="Horário"
          value={
            <span className="font-bold tabular-nums text-zinc-900 dark:text-foreground">
              {appointment.startTime} – {appointment.endTime}
            </span>
          }
        />
      </dl>

      <div className="my-3 h-px w-full bg-zinc-100 dark:bg-border" />

      <dl className={cn(GRADE, "text-[12px]")}>
        {appointment.procedure && (
          <InfoLine label="Procedimento" value={appointment.procedure} />
        )}
        {appointment.dentistName && (
          <InfoLine label="Profissional" value={appointment.dentistName} />
        )}
        {appointment.sala && <InfoLine label="Sala" value={appointment.sala.nome} />}
        {readOnly && organizationName && (
          <InfoLine label="Organização" value={organizationName} />
        )}
        {appointment.patientPhone && (
          <InfoLine
            label="Telefone"
            value={
              <span className="inline-flex items-center gap-1.5">
                <span className="tabular-nums">{formatPhone(appointment.patientPhone)}</span>
                {whatsappUrl && (
                  <a
                    href={whatsappUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex h-4 w-4 items-center justify-center rounded-full bg-emerald-500 text-white transition-colors hover:bg-emerald-600"
                    title="Enviar WhatsApp"
                    aria-label="WhatsApp"
                  >
                    <WhatsAppIcon className="h-2.5 w-2.5" />
                  </a>
                )}
              </span>
            }
          />
        )}
        {appointment.notes && (
          <InfoLine
            label="Observações"
            value={
              <span className="whitespace-pre-wrap break-words">{appointment.notes}</span>
            }
          />
        )}
        {appointment.openProsthesisOrders && appointment.openProsthesisOrders.length > 0 && (
          <InfoLine
            label="Prótese"
            value={
              <span className="flex flex-col items-start gap-1">
                {appointment.openProsthesisOrders.map((o) => (
                  <span key={o.id} className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5">
                    {/* Data antes do selo: com tabular-nums, datas e selos ficam em colunas. */}
                    <span className="whitespace-nowrap tabular-nums text-zinc-500 dark:text-muted-foreground">
                      {o.deadline ? `prev. ${previsao(o.deadline)}` : "sem prazo"}
                    </span>
                    <span
                      className="whitespace-nowrap rounded-full px-1.5 py-0.5 text-[10px] font-bold uppercase leading-none"
                      style={{ backgroundColor: STATUS_META[o.status].bg, color: STATUS_META[o.status].color }}
                    >
                      {STATUS_META[o.status].label}
                    </span>
                  </span>
                ))}
              </span>
            }
          />
        )}
        {appointment.createdAt && (
          <InfoLine
            label="Agendado em"
            value={
              <span className="tabular-nums text-zinc-500 dark:text-muted-foreground">
                {format(new Date(appointment.createdAt), "dd/MM/yyyy 'às' HH:mm", {
                  locale: ptBR,
                })}
              </span>
            }
          />
        )}
      </dl>

      <div className="my-3 h-px w-full bg-zinc-100 dark:bg-border" />

      {/* Ações */}
      <div className="flex flex-col gap-1.5">
        {readOnly ? (
          appointment.patientId && (
            <button
              type="button"
              onClick={onEdit}
              className="inline-flex h-9 items-center justify-center gap-2 rounded-md bg-brand-600 text-[13px] font-semibold text-white shadow-sm transition-colors hover:bg-brand-700"
            >
              Ver ficha do paciente
            </button>
          )
        ) : (
          <>
            {/* Atendimento vem antes de editar: durante a consulta é a ação do momento. Some em
                consulta cancelada ou faltou, onde não há o que atender. */}
            {chegada ? (
              <button
                type="button"
                onClick={() => onStatusChange("WAITING")}
                className="inline-flex h-9 items-center justify-center gap-2 rounded-md bg-brand-600 text-[13px] font-semibold text-white shadow-sm transition-colors hover:bg-brand-700"
              >
                <UserCheck className="h-3.5 w-3.5" />
                Paciente chegou
              </button>
            ) : onIniciarAtendimento && !FORA_DE_ATENDIMENTO.includes(appointment.status) && (
              appointment.status === "IN_PROGRESS" ? (
                <button
                  type="button"
                  disabled={atendimentoPendente}
                  onClick={onEncerrarAtendimento}
                  className="inline-flex h-9 items-center justify-center gap-2 rounded-md bg-emerald-600 text-[13px] font-semibold text-white shadow-sm transition-colors hover:bg-emerald-700 disabled:opacity-60"
                >
                  <Square className="h-3.5 w-3.5" />
                  Encerrar atendimento
                </button>
              ) : (
                <button
                  type="button"
                  disabled={atendimentoPendente}
                  onClick={onIniciarAtendimento}
                  className="inline-flex h-9 items-center justify-center gap-2 rounded-md bg-brand-600 text-[13px] font-semibold text-white shadow-sm transition-colors hover:bg-brand-700 disabled:opacity-60"
                >
                  <Play className="h-3.5 w-3.5" />
                  Iniciar atendimento
                </button>
              )
            )}
            {appointment.patientId && (
              <Link
                to={`/pacientes/${appointment.patientId}`}
                className="inline-flex h-9 items-center justify-center gap-2 rounded-md border border-zinc-200 text-[13px] font-semibold text-zinc-700 transition-colors hover:bg-zinc-50 dark:border-border dark:text-foreground dark:hover:bg-muted"
              >
                <User className="h-3.5 w-3.5" />
                Ver paciente
              </Link>
            )}
            <button
              type="button"
              onClick={onEdit}
              className={cn(
                "inline-flex h-9 items-center justify-center gap-2 rounded-md text-[13px] font-semibold transition-colors",
                onIniciarAtendimento && !FORA_DE_ATENDIMENTO.includes(appointment.status)
                  ? "border border-zinc-200 text-zinc-700 hover:bg-zinc-50 dark:border-border dark:text-foreground dark:hover:bg-muted"
                  : "bg-brand-600 text-white shadow-sm hover:bg-brand-700",
              )}
            >
              <Pencil className="h-3.5 w-3.5" />
              Editar consulta
            </button>
            <button
              type="button"
              onClick={onDelete}
              className="inline-flex h-8 items-center justify-center gap-1.5 rounded-md text-[11px] font-medium text-red-600 transition-colors hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-500/10"
            >
              <Trash2 className="h-3 w-3" />
              Excluir
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function StatusItem({
  status,
  current,
  onSelect,
}: {
  status: AppointmentStatus;
  current: AppointmentStatus;
  onSelect: (s: AppointmentStatus) => void;
}) {
  const color = APPOINTMENT_STATUS_COLORS[status];
  const isActive = status === current;
  // Hover dinâmico na cor do status — Tailwind não gera classes dinâmicas,
  // então aplicamos via inline style nos handlers.
  const hoverStyle: CSSProperties = {
    backgroundColor: `${color}1f`,
    color: color,
  };
  return (
    <DropdownMenuItem
      onClick={() => onSelect(status)}
      className="flex cursor-pointer items-center gap-2.5 rounded-md px-2.5 py-2 text-[12px] font-medium outline-none transition-colors"
      onMouseEnter={(e) => {
        Object.assign(e.currentTarget.style, hoverStyle);
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = "";
        e.currentTarget.style.color = "";
      }}
    >
      <span
        className="h-2.5 w-2.5 shrink-0 rounded-full ring-2 ring-white dark:ring-popover"
        style={{ backgroundColor: color }}
      />
      <span className="flex-1 text-left">{APPOINTMENT_STATUS_LABELS[status]}</span>
      {isActive && (
        <span
          className="text-[9px] font-bold uppercase tracking-wider"
          style={{ color }}
        >
          Atual
        </span>
      )}
    </DropdownMenuItem>
  );
}

/** Rótulo numa coluna fixa e valor ao lado: as duas listas do popover começam na mesma margem. */
const GRADE = "grid grid-cols-[80px_1fr] gap-x-2.5 gap-y-1.5 leading-snug";

function InfoLine({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <>
      <dt className="font-medium text-zinc-500 dark:text-muted-foreground">{label}</dt>
      <dd className="min-w-0 text-zinc-800 dark:text-foreground">{value}</dd>
    </>
  );
}

/** Dia e mês; o ano só quando não é o corrente, pra caber antes do selo. */
function previsao(v: Date | string): string {
  const d = new Date(v);
  const outroAno = d.getFullYear() !== new Date().getFullYear();
  return d.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit", ...(outroAno && { year: "numeric" }) });
}

function formatPhone(v: string): string {
  const d = v.replace(/\D/g, "");
  if (d.length === 11) return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
  if (d.length === 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return v;
}

function WhatsAppIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
    </svg>
  );
}
